
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Laptop, Smartphone, Tablet } from "lucide-react";
import { MockBadge } from "@/components/ui/mock-badge";

interface DeviceData {
  device: string;
  count: number;
}

interface DeviceAnalyticsProps {
  deviceData?: DeviceData[];
  isLoading: boolean;
}

export function DeviceAnalytics({ deviceData, isLoading }: DeviceAnalyticsProps) {
  // Mock data for development until phase 4 integration is complete
  const mockDeviceData = [
    { device: "Mobile", count: 65 },
    { device: "Desktop", count: 30 },
    { device: "Tablet", count: 5 },
  ];

  const data = deviceData || mockDeviceData;
  
  const chartData = {
    labels: data.map(item => item.device),
    datasets: [
      {
        label: 'Device Usage',
        data: data.map(item => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
        ],
        borderColor: [
          'rgb(255, 99, 132)',
          'rgb(54, 162, 235)',
          'rgb(255, 206, 86)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">Device Breakdown</CardTitle>
            <CardDescription>Which devices are used to open emails</CardDescription>
          </div>
          <MockBadge 
            tooltip="Device analytics currently displays mock data and does not reflect actual user devices."
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="w-full h-[200px]" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-1 md:col-span-2">
              <div className="h-[200px]">
                <PieChart data={chartData} />
              </div>
            </div>
            <div className="space-y-4">
              {data.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  {item.device === "Mobile" && <Smartphone className="h-5 w-5 text-pink-500" />}
                  {item.device === "Desktop" && <Laptop className="h-5 w-5 text-blue-500" />}
                  {item.device === "Tablet" && <Tablet className="h-5 w-5 text-yellow-500" />}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">{item.device}</span>
                      <span className="text-sm text-gray-500">{item.count}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                      <div 
                        className={`h-2.5 rounded-full ${
                          item.device === "Mobile" ? "bg-pink-500" : 
                          item.device === "Desktop" ? "bg-blue-500" : "bg-yellow-500"
                        }`} 
                        style={{ width: `${item.count}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
